import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Flex, Radio } from "antd";
import { createStyles } from "antd-style";

import { type JobPostScreeningQuestion } from "@av/apps/recruitment/ats-common/types";
import { Card } from "@av/components/atoms/Card";
import { Form } from "@av/components/atoms/form/Form";
import { Typography } from "@av/components/atoms/typography/Typography";
import type { SupportedLocales } from "@av/components/organisms/editor/localized-text-editor/text-editor-supported-locales";
import { useLocaleStore } from "@av/utils/hooks/useLocaleStore";

const useStyles = createStyles(({ css }) => ({
  answerFormItem: css`
    margin-bottom: 0;
  `,
  questionText: css`
    display: block;
    width: 100%;
  `,
}));

export function QuestionsCard(props: { questions: JobPostScreeningQuestion[] }) {
  const { t } = useTranslation();
  const { styles } = useStyles();

  const { questions } = props;
  const { locale } = useLocaleStore();
  const localeVal = locale as SupportedLocales;

  const form = Form.useFormInstance();

  useEffect(() => {
    form.setFieldsValue({
      screeningAnswers: questions.map((question) => ({
        screeningQuestionId: question.screeningQuestionId,
        version: question.version,
      })),
    });
  }, [form, questions]);

  if (!questions || !questions.length) {
    return null;
  }

  return (
    <Card
      title={t("recruitment.jobApplication.section_questions", { count: questions.length })}
      testIdPrefix="candidate-application-questions"
    >
      <Form.List name={["screeningAnswers"]}>
        {(fields) => (
          <Flex vertical gap={16}>
            {fields.map((field, index) => {
              const question = questions[index];

              if (!question) return null;

              return (
                <Flex key={field.key} vertical gap={8}>
                  <Form.Item
                    name={`${index}.screeningQuestionId`}
                    initialValue={question.screeningQuestionId}
                    hidden
                    testIdPrefix={`candidate-application-screening-questionId-${index + 1}`}
                  />

                  <Form.Item
                    name={`${index}.version`}
                    initialValue={question.version}
                    hidden
                    testIdPrefix={`candidate-application-screening-version-${index + 1}`}
                  />

                  <Typography.Text
                    className={styles.questionText}
                    testIdPrefix={`candidate-application-screening-question-${index + 1}`}
                    strong
                  >
                    {question.questions[localeVal] ?? question.questions["en-CA"]}
                  </Typography.Text>

                  <Form.Item
                    name={`${index}.actualBooleanAnswer`}
                    testIdPrefix={`candidate-application-screening-answer-${index + 1}`}
                    labelString={t("recruitment.jobApplication.label_answer")}
                    required
                    className={styles.answerFormItem}
                  >
                    <Radio.Group
                      data-testid={`candidate-application-screening-answer-${index + 1}-radio-group`}
                      size="middle"
                    >
                      <Radio.Button
                        data-testid={`candidate-application-screening-answer-${index + 1}-yes`}
                        value={true}
                      >
                        {t("recruitment.common.global_yes")}
                      </Radio.Button>
                      <Radio.Button
                        data-testid={`candidate-application-screening-answer-${index + 1}-no`}
                        value={false}
                      >
                        {t("recruitment.common.global_no")}
                      </Radio.Button>
                    </Radio.Group>
                  </Form.Item>
                </Flex>
              );
            })}
          </Flex>
        )}
      </Form.List>
    </Card>
  );
}
